// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * modes.ts — three rounds with genuinely different shapes.
 *
 * A mode has to change how the game PLAYS, not a number on a HUD:
 *  - Trim      10×20, empty, medium keel — the balanced one.
 *  - Careen     8×22, low keel, fast drift — one piece swings the list hard and
 *               the far column is out of reach constantly. A knife fight.
 *  - Broadside 14×18, high keel, but you START out of trim with heavy cargo in
 *               the well and a line costs 14 cells. Trim is the whole job.
 *
 * The host's pick travels FROZEN inside the round start (see session.ts
 * roundOpts) — a setting each peer reads from its own UI is a setting two peers
 * can disagree about, and here it changes the board size.
 */

export interface Mode {
  id: string;
  name: string;
  blurb: string;
  cols: number;
  rows: number;
  /**
   * How much the well resists listing — a built-in centred counterweight added
   * to the torque denominator. A big keel means a lopsided stack barely tilts.
   * It SCALES the list; it does not decide whether the mechanic fires.
   */
  keel: number;
  /**
   * |list| beyond which a falling piece starts sliding downhill.
   *
   * THIS is the load-bearing constant, which the sweep had to tell me — the
   * plan named `keel` and the plan was wrong. Sweeping keel alone moved the
   * outcome around noisily; sweeping the threshold moved it monotonically,
   * because the threshold is what decides whether drift ever fires at all.
   * Pinned by its own assertion in balance.test.ts.
   */
  listThreshold: number;
  /** Rows of descent that buy one column of drift. */
  driftRows: number;
  /** Most columns one piece may drift, however far it falls. */
  maxDrift: number;
  /** Bags between ingots (the single heavy cell). */
  ingotEvery: number;
  /** Heavy cargo cells scattered in the well at the start. */
  cargo: number;
  /** ms per row at level 1. */
  gravityBase: number;
  /** Multiplied per level. */
  gravityRamp: number;
  /** Floor for the above. */
  gravityMin: number;
}

export const MODES: Mode[] = [
  {
    id: 'trim',
    name: 'Trim',
    blurb: '10 × 20 · the balanced well',
    cols: 10,
    rows: 20,
    keel: 44,
    listThreshold: 0.22,
    driftRows: 6,
    maxDrift: 2,
    ingotEvery: 3,
    cargo: 0,
    gravityBase: 1000,
    gravityRamp: 0.85,
    gravityMin: 70,
  },
  {
    id: 'careen',
    name: 'Careen',
    blurb: '8 × 22 · narrow, twitchy, always listing',
    cols: 8,
    rows: 22,
    keel: 40,
    listThreshold: 0.22,
    driftRows: 6,
    maxDrift: 2,
    ingotEvery: 2,
    cargo: 0,
    gravityBase: 820,
    gravityRamp: 0.82,
    gravityMin: 60,
  },
  {
    id: 'broadside',
    name: 'Broadside',
    blurb: '14 × 18 · wide, slow, loaded with cargo',
    cols: 14,
    rows: 18,
    keel: 70,
    listThreshold: 0.14,
    driftRows: 7,
    maxDrift: 2,
    ingotEvery: 4,
    cargo: 10,
    gravityBase: 1150,
    gravityRamp: 0.88,
    gravityMin: 90,
  },
];

export const DEFAULT_MODE = MODES[0];

/**
 * Resolve a mode id that may have come off the wire. An unknown id must fall
 * back to the default, never reach the generator as `undefined` — and
 * `Object.hasOwn`-equivalent lookup semantics matter here because a wire-keyed
 * plain-object lookup lets 'constructor' through as a Mode of undefined fields.
 */
export function modeOf(id: unknown): Mode {
  if (typeof id !== 'string') return DEFAULT_MODE;
  return MODES.find((m) => m.id === id) ?? DEFAULT_MODE;
}

/** ms between gravity steps at a given level. */
export function dropInterval(mode: Mode, level: number): number {
  const ms = mode.gravityBase * Math.pow(mode.gravityRamp, Math.max(0, level - 1));
  return Math.max(mode.gravityMin, ms);
}

/** The gap punched in an incoming ballast row, on the receiver's LIGHT side. */
export function ballastGap(mode: Mode): number {
  return Math.max(2, Math.round(mode.cols * 0.25));
}
